import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Plus, Trophy, MapPin, Users, Loader2, RefreshCw, X, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall, type Event } from '@/lib/api';

const TYPES = ['tournament', 'meet', 'camp', 'other'] as const;

export default function Events() {
  const { getToken } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState<'upcoming' | 'past' | 'all'>('upcoming');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const query: Record<string, string> = {};
      if (view === 'upcoming') query.upcoming = '1';
      const data = await apiCall<{ items: Event[] }>('/v1/events', { bearerToken: token, query });
      let items = data.items;
      if (view === 'past') items = items.filter((e) => new Date(e.starts_at) < new Date());
      setEvents(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [getToken, view]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4 max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Trophy className="h-6 w-6" /> Events</h1>
          <p className="text-sm text-muted-foreground">Tournaments, meets, and camps. Register members and record results.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add event
          </Button>
        </div>
      </div>

      <div className="border-b flex items-center gap-1">
        {(['upcoming', 'past', 'all'] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px capitalize ' +
              (view === v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')
            }
          >{v}</button>
        ))}
      </div>

      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-3 text-sm text-destructive flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> {error}
          </CardContent>
        </Card>
      )}

      {events.length === 0 && !loading && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          <Trophy className="mx-auto mb-3 h-8 w-8 opacity-40" />
          No {view === 'upcoming' ? 'upcoming' : view === 'past' ? 'past' : ''} events yet. Click <strong>Add event</strong> to create one.
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {events.map((e) => <EventRow key={e.id} event={e} />)}
      </div>

      {showCreate && (
        <CreateEventDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); void load(); }}
        />
      )}
    </div>
  );
}

function EventRow({ event }: { event: Event }) {
  const start = new Date(event.starts_at);
  const end = event.ends_at ? new Date(event.ends_at) : null;
  const sameDay = !end || start.toDateString() === end.toDateString();
  const dateLabel = sameDay
    ? start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end!.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const past = start < new Date();

  return (
    <Link to={`/dashboard/events/${event.id}`}>
      <Card className="hover:bg-muted/40 transition-colors cursor-pointer">
        <CardContent className="py-3 flex items-center gap-4 flex-wrap">
          <div className="text-center min-w-[64px]">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">
              {start.toLocaleDateString(undefined, { month: 'short' })}
            </div>
            <div className="text-2xl font-bold tabular-nums leading-none">{start.getDate()}</div>
            <div className="text-xs text-muted-foreground">{start.getFullYear()}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{event.name}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-3 flex-wrap mt-0.5">
              <span>{dateLabel}</span>
              {event.location_name && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {event.location_name}</span>}
              {event.sanctioning_body && <Badge variant="outline" className="text-xs">{event.sanctioning_body}</Badge>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {event.registered_count ?? 0}</div>
              <div className="text-xs text-muted-foreground">registered</div>
            </div>
            <Badge variant={event.status === 'completed' ? 'outline' : event.status === 'cancelled' ? 'destructive' : past ? 'outline' : 'default'}>
              {event.status === 'scheduled' && past ? 'pending results' : event.status}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function CreateEventDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { getToken } = useAuth();
  const [name, setName] = useState('');
  const [type, setType] = useState<typeof TYPES[number]>('tournament');
  const [sanctioning, setSanctioning] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [location, setLocation] = useState('');
  const [address, setAddress] = useState('');
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const token = await getToken();
      await apiCall('/v1/events', {
        method: 'POST',
        bearerToken: token,
        body: {
          name: name.trim(),
          type,
          sanctioning_body: sanctioning.trim() || null,
          starts_at: new Date(startsAt).toISOString(),
          ends_at: endsAt ? new Date(endsAt).toISOString() : null,
          location_name: location.trim() || null,
          location_address: address.trim() || null,
          url: url.trim() || null,
          notes: notes.trim() || null,
        },
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add event</h2>
              <Button type="button" variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
            <Field label="Name *"><input value={name} onChange={(e) => setName(e.target.value)} required className="w-full border rounded px-2 py-1 text-sm" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type"><select value={type} onChange={(e) => setType(e.target.value as typeof TYPES[number])} className="w-full border rounded px-2 py-1 text-sm">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
              <Field label="Sanctioning body"><input value={sanctioning} onChange={(e) => setSanctioning(e.target.value)} placeholder="BAWA / SJWA / USAW" className="w-full border rounded px-2 py-1 text-sm" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts at *"><input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} required className="w-full border rounded px-2 py-1 text-sm" /></Field>
              <Field label="Ends at"><input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" /></Field>
            </div>
            <Field label="Location"><input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Heritage High School" className="w-full border rounded px-2 py-1 text-sm" /></Field>
            <Field label="Address"><input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="101 American Ave, Brentwood CA" className="w-full border rounded px-2 py-1 text-sm" /></Field>
            <Field label="Registration URL"><input type="url" value={url} onChange={(e) => setUrl(e.target.value)} className="w-full border rounded px-2 py-1 text-sm" /></Field>
            <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full border rounded px-2 py-1 text-sm" /></Field>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={!name.trim() || !startsAt || submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create
              </Button>
            </div>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block text-sm"><span className="block text-xs text-muted-foreground mb-1">{label}</span>{children}</label>;
}
